import type {
  PlanScope,
  ProductPlan,
  ProductPlanCreateRq,
  ProductPlanOption,
  ProductPlanOptionCreateRq,
  ProductPlanUpdateRq,
  ProductPlanUsageMetric,
  ProductPlanUsageMetricCreateRq,
  Software,
} from '../software.types';

export class ProductPlanModel implements ProductPlan {
  private readonly _dto: ProductPlan;

  constructor(dto?: ProductPlan) {
    this._dto = dto || {} as ProductPlan;
  }

  get originDTO(): ProductPlan {
    return this._dto;
  }

  product?: Software | undefined;

  get isInUseByTenant(): boolean {
    return this._dto.isInUseByTenant || false;
  }

  public get planId(): string {
    return this._dto.planId || '';
  }

  public get planName(): string {
    return this._dto.planName || '';
  }

  public get planType(): 'MAIN' | 'ADD_ON' {
    return this._dto.planType || 'MAIN';
  }

  public get featureSet(): string {
    return this._dto.featureSet || '';
  }

  public get description(): string {
    return this._dto.description || '';
  }

  public get pricingType():
    | 'Free'
    | 'FLAT_RATE'
    | 'USAGE_BASED'
    | 'USAGE_COMMITMENT'
    | 'PER_USER'
    | 'PER_ACTIVE_USER'
    | 'PER_CONCURRENT_USER'
    | 'BY_USAGE_RANGE'
    | 'CUSTOM' {
    return this._dto.pricingType || 'Free';
  }

  public get isPricingByUser() {
    // unitPrice로 계산
    return (
      this.pricingType === 'PER_ACTIVE_USER' ||
      this.pricingType === 'PER_CONCURRENT_USER' ||
      this.pricingType === 'PER_USER'
    );
  }

  public get isPricingByUsage() {
    // usageMetricPrices로 계산
    return (
      this.pricingType === 'Free' ||
      this.pricingType === 'FLAT_RATE' ||
      this.pricingType === 'USAGE_BASED' ||
      this.pricingType === 'USAGE_COMMITMENT' ||
      this.pricingType === 'BY_USAGE_RANGE'
    );
  }

  get pricingTypeDisplayName(): string {
    if (this.isPricingByUser) return '사용자 수';
    return '';
  }

  public get usageMetricList(): ProductPlanUsageMetric[] {
    return this._dto.usageMetricList || [];
  }

  public get planOptionList(): ProductPlanOption[] {
    return (this._dto.planOptionList || []).filter(({ optionName }) => !!optionName);
  }

  public get currencyUnit(): CurrencyUnit {
    return this._dto.currencyUnit || 'USD';
  }

  public get isBasicPlan(): boolean {
    return this._dto.isBasicPlan || false;
  }

  public get planScope(): PlanScope {
    return this._dto.planScope || 'SUBSCRIPTION';
  }

  public get planGrade(): number {
    return this._dto.planGrade || 0;
  }

  public get usageMetrics() {
    return this.usageMetricList.map(({ usageMetric }) => usageMetric);
  }

  public createRq(softwareId: string): ProductPlanCreateRq {
    return {
      softwareId,
      planName: this._dto.planName || '',
      planType: this._dto.planType || 'MAIN',
      planGrade: this._dto.planGrade,
      description: this._dto.description,
      featureSet: this._dto.featureSet,
      pricingType: this._dto.pricingType || 'Free',
      usageMetricList: this.toProductPlanUsageMetricCreateList(),
      planOptionList: this.toPlanOptionCreateList(),
      currencyUnit: this._dto.currencyUnit || 'USD',
      isBasicPlan: this._dto.isBasicPlan || false,
      planScope: this._dto.planScope,
    };
  }

  public get updateRq(): ProductPlanUpdateRq {
    return {
      planId: this._dto.planId || '',
      planName: this._dto.planName || '',
      planType: this._dto.planType || 'MAIN',
      planGrade: this._dto.planGrade,
      description: this._dto.description,
      featureSet: this._dto.featureSet,
      pricingType: this._dto.pricingType || 'Free',
      usageMetricList: this.toProductPlanUsageMetricCreateList(),
      planOptionList: this.toPlanOptionCreateList(),
      currencyUnit: this._dto.currencyUnit || 'USD',
      isBasicPlan: this._dto.isBasicPlan || false,
      planScope: this._dto.planScope,
    };
  }

  public get usageMetricCreateList() {
    return this.toProductPlanUsageMetricCreateList();
  }

  toProductPlanUsageMetricCreateList(): ProductPlanUsageMetricCreateRq[] {
    return (this._dto.usageMetricList || []).map(productPlanUsageMetric => ({
      usageMetricId: productPlanUsageMetric.usageMetric.usageMetricId,
      billableUsageExpression: productPlanUsageMetric.billableUsageExpression,
      costExpression: productPlanUsageMetric.costExpression,
      committedUsage: productPlanUsageMetric.committedUsage,
      details: productPlanUsageMetric.details,
      costCalculationType: productPlanUsageMetric.costCalculationType,
    }));
  }

  toPlanOptionCreateList(): ProductPlanOptionCreateRq[] {
    return (this._dto.planOptionList || []).map(planOption => ({
      planOptionId: planOption.planOptionId,
      optionName: planOption.optionName,
      paymentPeriod: planOption.paymentPeriod,
      paymentOption: planOption.paymentOption,
      billingCycle: planOption.billingCycle,
      billingPeriodType: planOption.billingPeriodType,
      commitmentPeriod: planOption.commitmentPeriod,
      customCommitmentPeriod: planOption.customCommitmentPeriod,
      unitPeriod: planOption.unitPeriod,
      unitPrice: planOption.unitPrice,
      usageMetricPrices: planOption.usageMetricPrices.map(usageMetricPrice => ({
        usageMetricId: usageMetricPrice.usageMetricId,
        usageFrom: usageMetricPrice.usageFrom,
        usageTo: usageMetricPrice.usageTo,
        usageUnitPrice: usageMetricPrice.usageUnitPrice,
        unitPeriod: usageMetricPrice.unitPeriod,
      })),
    }));
  }
}
